import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IWidget } from '../../core/models/widget.model';
import { AppService } from '../../store/app.service';
import { Options, SortableEvent } from 'sortablejs';
import { IPermission, IUser } from '../../core/models/auth.models';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as util from '../../core/helpers/utility';
import { UtilityService } from '../../core/helpers/utility.service';
import { ROLES } from '../../core/helpers/constants';
import { ICampaign } from '../../core/models/campaigns.model';
import { InstanceService } from '../../core/instance/instance.service';

type DashboardTabId =
  | 'tasks'
  | 'dashboard'
  | 'salesPipeline'
  | 'dmsDashboard'
  | 'oldDmsDashboard'
  | 'widgets'
  | 'overview'
  | 'rollupReport'
  | 'campaignReports'
  | 'managerDash';

interface DashboardTab {
  id: DashboardTabId;
  titleKey: string;
}

const DASHBOARD_TAB_TITLE_KEYS: Record<DashboardTabId, string> = {
  tasks: 'TASKS',
  dashboard: 'DASHBOARD',
  salesPipeline: 'SALES_PIPELINE',
  dmsDashboard: 'DMS_DASHBOARD',
  oldDmsDashboard: 'OLD_DMS_DASH',
  widgets: 'WIDGETS',
  overview: 'OVERVIEW',
  rollupReport: 'ROLLUP_REPORT',
  campaignReports: 'CAMPAIGN_REPORTS',
  managerDash: 'MANAGER_DASH',
};

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})

/**
 *  Dashboard Component
 */
export class DashboardComponent implements OnInit, OnDestroy {
  ROLES = ROLES;
  activeUser: IUser = {} as IUser;
  public userRoles: Array<number> = [];
  doWhat: IPermission = {} as IPermission;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  userId: any = '';
  userAuthToken: string = '';
  isAdmin: boolean = false;
  externalUser: boolean = false;
  iframeSrc: SafeResourceUrl= '';

  dashboardHeight: string = '85vh';

  // Tabs
  tabs: Array<DashboardTab> = this.buildTabs(['tasks', 'dashboard', 'widgets']);
  activeTabId: DashboardTabId = this.tabs[0].id;

  // Widgets
  widgets!: IWidget[];
  changeLayoutStarted: boolean = false;

  selectedCampaignId: string = '';
  campaignsData = new Array<ICampaign>();
  selectedRollupCompanyId: string = '';

  @Input() iReportFormat: string = '';

  sortableOptions: Options = {
    group: 'test',
    handle: '.draghandler',
    onUpdate:(event) =>  this.onWidgetDrop(event),
    dataIdAttr: 'id'
  };

  constructor(private appService: AppService, 
    private sanitizer: DomSanitizer, 
    public uService: UtilityService,
    private instance: InstanceService) {

    this.userRoles = [...this.uService.getUserRoles()];
    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        this.activeUser = u;
      }
    );

    this.appService.getChangeLayoutValue()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: boolean) => {
        this.changeLayoutStarted = response;
      });
  }

  ngOnInit(): void {
    this.appService.setPageTitle$('HOME');
    this.userAuthToken = util.getAuthToken();
    this.userId = this.activeUser.id;

    if (this.instance.isAltLayout) {
      this.tabs = this.buildTabs(['overview', 'rollupReport', 'campaignReports']);
      this.activeTabId = 'overview';
    }
    else if (this.userRoles.includes(ROLES.Admin.Code) || this.userRoles.includes(ROLES.TechServices.Code)) {
      this.isAdmin = true;
      this.tabs = this.buildTabs(['tasks', 'salesPipeline', 'dmsDashboard', 'widgets', 'overview', 'rollupReport', 'campaignReports']);
      this.activeTabId = this.tabs[0].id;
    }
    else if (this.userRoles.includes(ROLES.SalesRep.Code)) {
      this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(`https://prospects.reach.marketing/inside/?authToken=${this.userAuthToken}&userId=${this.userId}&role=${this.userRoles[0]}`);
    }
    else if (this.userRoles.includes(ROLES.DMS.Code)) {
      this.tabs = this.buildTabs(['dmsDashboard', 'widgets', 'overview', 'rollupReport', 'campaignReports']);
      this.activeTabId = 'dmsDashboard';
      this.iframeSrc = '';
    }
    else if (this.userRoles.includes(ROLES.ShopManager.Code) || this.userRoles.includes(ROLES.Staff.Code)) {
      this.tabs = this.buildTabs(['tasks', 'widgets', 'overview', 'rollupReport', 'campaignReports']);
      this.activeTabId = this.tabs[0].id;
    }
    else {
      // External user
      this.externalUser = true;
      this.tabs = this.buildTabs(['overview', 'rollupReport', 'campaignReports']);
      this.activeTabId = 'overview';
    }

    this.appService.getWidgets$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: IWidget[]) => {
        this.widgets = response.sort((a, b) => a.order - b.order);
      });
    this.appService.fetchWidgets();
  }

  private buildTabs(ids: Array<DashboardTabId>): Array<DashboardTab> {
    return ids.map(id => ({ id, titleKey: DASHBOARD_TAB_TITLE_KEYS[id] }));
  }

  selectTab(tabId: DashboardTabId) {
    this.activeTabId = tabId;

    if (this.activeTabId === 'campaignReports') {
      this.loadCampaignReportsCampaigns();
    }

    // Legacy DMS dashboard is an iframe; allow both Admins and DMS users to view it.
    if (this.activeTabId === 'oldDmsDashboard') {
      this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://dms.reach.marketing/testgrounds/?authToken=${this.userAuthToken}&userId=${this.userId}`
      );
      return;
    }

    // Admin-only iframe tabs.
    if (this.isAdmin && (this.activeTabId === 'salesPipeline' || this.activeTabId === 'dashboard')) {
      this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://prospects.reach.marketing/inside/?authToken=${this.userAuthToken}&userId=${this.userId}&role=${this.userRoles[0]}`
      );
    }
  }

  private loadCampaignReportsCampaigns() {
    if (this.campaignsData?.length) {
      return;
    }

    this.appService.getAllCampaigns$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.campaignsData = [...(response?.data ?? [])];
      });

    this.appService.fetchAllCampaigns({});
  }

  onWidgetDrop(event: SortableEvent) {
    const widgetId = event.item.id;
    const newOrder = event.newIndex! + 1;
    this.appService.reorderWidget(widgetId, newOrder);
  }

  updateReportView(campaignId: any) {
    this.selectedCampaignId = campaignId;
  }

  changeLayoutClick() {
    this.appService.changeLayout(!this.changeLayoutStarted);
  }

  addWidgetClick() {
    this.appService.addWidget$();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
